import { useState, useEffect } from 'react';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import FileUpload from '../ui/FileUpload';
import LessonContentModal from '../ui/LessonContentModal';
import { lessonAPI, courseAPI, lessonContentAPI, losAPI } from '../../services/api';

const Lessons = () => {
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [los, setLos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isLOSModalOpen, setIsLOSModalOpen] = useState(false);
  const [selectedLessonForContent, setSelectedLessonForContent] = useState(null);
  const [selectedLessonForLOS, setSelectedLessonForLOS] = useState(null);
  const [lessonLOS, setLessonLOS] = useState([]);
  const [losLoading, setLosLoading] = useState(false);
  const [deletingLOSId, setDeletingLOSId] = useState(null);
  const [editingLOSId, setEditingLOSId] = useState(null);
  const [editingLOSValue, setEditingLOSValue] = useState('');
  const [updatingLOSId, setUpdatingLOSId] = useState(null);
  const [isViewingContents, setIsViewingContents] = useState(false);
  const [existingLessonContents, setExistingLessonContents] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [deletingLessonId, setDeletingLessonId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    course_id: '',
    order_index: 0,
    video_url: '',
    los: [
      { name: '', id: null }  // Each LOS can have an optional id if it exists in DB

    ],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sortable: true
    },
    {
      accessorKey: 'description',
      header: 'Description',
      sortable: false
    },
    {
      accessorKey: 'course_title',
      header: 'Course',
      sortable: true
    },
    {
      accessorKey: 'order_index',
      header: 'Order',
      sortable: true
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      sortable: true,
      cell: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-32',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleEditLesson(row)}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="Edit lesson"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleViewLessonContents(row)}
            className="p-1.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            title="View lesson contents"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => handleCreateLessonContent(row)}
            className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
            title="Create lesson content"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={() => handleViewLOS(row)}
            className="p-1.5 text-yellow-500 hover:text-yellow-700 hover:bg-yellow-50 rounded-full transition-colors"
            title="View Learning Outcome Standards (LOS)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteLesson(row.id)}
            disabled={deletingLessonId === row.id}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete lesson"
          >
            {deletingLessonId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchLessons();
    fetchCourses();
  }, []);

  // Ensure courses are loaded when modal opens
  useEffect(() => {
    if (isModalOpen && courses.length === 0) {
      fetchCourses();
    }
  }, [isModalOpen]);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const response = await lessonAPI.getAllLessons();
      setLessons(response.data || []);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLessons([]);
    } finally {
      setLoading(false);
    }
  };
       
       const handleInputChange = (e) => {
         const { name, value, type } = e.target;
         setFormData(prev => ({
           ...prev,
           [name]: type === 'number' ? parseInt(value) || 0 : value
         }));
       };

       const handleVideoUploadSuccess = (url) => {
         setFormData(prev => ({
           ...prev,
           video_url: url
         }));
       };

       const handleVideoUploadError = (error) => {
         console.error('Video upload error:', error);
       };

       const handleLOSChange = (index, value) => {
         setFormData(prev => ({
           ...prev,
           los: prev.los.map((los, i) => 
             i === index ? { ...los, name: value } : los
           )
         }));
       };

       const addLOS = () => {
         setFormData(prev => ({
           ...prev,
           los: [...prev.los, { name: '' }]
         }));
       };

       const removeLOS = (index) => {
         if (formData.los.length > 1) {
           setFormData(prev => ({
             ...prev,
             los: prev.los.filter((_, i) => i !== index)
           }));
         }
       };

       const createLOSForLesson = async (lessonId) => {
         try {
           console.log('Starting LOS creation for lesson:', lessonId);
           console.log('Form LOS data:', formData.los);
           
           const losToCreate = formData.los.filter(los => los.name.trim() !== '');
           console.log(`Filtered ${losToCreate.length} LOS entries to create (out of ${formData.los.length} total)`);
           
           if (losToCreate.length === 0) {
             console.log('No LOS to create - all entries are empty');
             return;
           }
           
           // Create LOS one by one with detailed logging
           let successCount = 0;
           for (let i = 0; i < losToCreate.length; i++) {
             const los = losToCreate[i];
             const losData = {
               lesson_id: lessonId,
               name: los.name.trim(),
               order_index: i
             };
             
             console.log(`Creating LOS ${i + 1}/${losToCreate.length}:`, losData);
             
             try {
               const response = await losAPI.createLOS(losData);
               console.log(`✓ Successfully created LOS ${i + 1}:`, response);
               successCount++;
             } catch (losError) {
               console.error(`✗ Failed to create LOS ${i + 1}:`, losError);
               console.error('Failed LOS data was:', losData);
             }
           }
           
           console.log(`LOS creation complete: ${successCount}/${losToCreate.length} successful`);
           
           // Clear LOS cache to ensure fresh data is fetched
           losAPI.clearLOSCache();
         } catch (error) {
           console.error('Error in createLOSForLesson:', error);
           // Don't throw error as lesson creation was successful
         }
       };

       const handleViewLOS = async (lesson) => {
         try {
           setSelectedLessonForLOS(lesson);
           setLosLoading(true);
           setIsLOSModalOpen(true);
           
           // Clear cache to ensure fresh data
           losAPI.clearLOSCache();
           
           // Fetch LOS for this lesson
           const response = await losAPI.getLOSByLessonId(lesson.id);
           console.log('LOS response:', response);
           setLessonLOS(response.data || []);
         } catch (error) {
           console.error('Error fetching LOS:', error);
           setLessonLOS([]);
         } finally {
           setLosLoading(false);
         }
       };

       const handleEditLOS = (los) => {
         setEditingLOSId(los.id);
         setEditingLOSValue(los.name);
       };

       const handleCancelEditLOS = () => {
         setEditingLOSId(null);
         setEditingLOSValue('');
       };

       const handleUpdateLOS = async (losId) => {
         if (!editingLOSValue.trim()) {
           alert('Learning Outcome Standard cannot be empty.');
           return;
         }

         try {
           setUpdatingLOSId(losId);
           
           // Find the current LOS to preserve order_index
           const currentLOS = lessonLOS.find(los => los.id === losId);
           
           console.log('Updating LOS:', losId, 'with data:', {
             name: editingLOSValue.trim(),
             lesson_id: selectedLessonForLOS.id,
             order_index: currentLOS?.order_index || 0
           });
           
           // Update the LOS
           await losAPI.updateLOS(losId, {
             name: editingLOSValue.trim(),
             lesson_id: selectedLessonForLOS.id,
             order_index: currentLOS?.order_index || 0
           });
           
           console.log('LOS update successful, clearing cache and refetching...');
           
           // Clear cache BEFORE fetching to ensure fresh data
           losAPI.clearLOSCache();
           
           // Add small delay to ensure cache is cleared
           await new Promise(resolve => setTimeout(resolve, 100));
           
           // Refresh LOS list with fresh data
           if (selectedLessonForLOS) {
             const response = await losAPI.getLOSByLessonId(selectedLessonForLOS.id);
             console.log('Fetched updated LOS list:', response.data);
             setLessonLOS(response.data || []);
           }
           
           // Clear editing state
           setEditingLOSId(null);
           setEditingLOSValue('');
           
         } catch (error) {
           console.error('Error updating LOS:', error);
           alert('Failed to update Learning Outcome Standard. Please try again.');
         } finally {
           setUpdatingLOSId(null);
         }
       };

       const handleDeleteLOS = async (losId) => {
         if (!window.confirm('Are you sure you want to delete this Learning Outcome Standard?')) {
           return;
         }

         try {
           setDeletingLOSId(losId);
           console.log('Deleting LOS:', losId);
           
           await losAPI.deleteLOS(losId);
           
           console.log('LOS deleted successfully, clearing cache and refetching...');
           
           // Clear cache BEFORE fetching
           losAPI.clearLOSCache();
           
           // Add small delay to ensure cache is cleared
           await new Promise(resolve => setTimeout(resolve, 100));
           
           // Refresh LOS list with fresh data
           if (selectedLessonForLOS) {
             const response = await losAPI.getLOSByLessonId(selectedLessonForLOS.id);
             console.log('Fetched updated LOS list after deletion:', response.data);
             setLessonLOS(response.data || []);
           }
           
         } catch (error) {
           console.error('Error deleting LOS:', error);
           alert('Failed to delete Learning Outcome Standard. Please try again.');
         } finally {
           setDeletingLOSId(null);
         }
       };

       const handleCloseLOSModal = () => {
         setIsLOSModalOpen(false);
         setSelectedLessonForLOS(null);
         setLessonLOS([]);
         setDeletingLOSId(null);
         setEditingLOSId(null);
         setEditingLOSValue('');
         setUpdatingLOSId(null);
       };

       const handleSubmit = async (e) => {
         e.preventDefault();

         try {
           setIsSubmitting(true);
           const submitData = {
             ...formData,
             course_id: parseInt(formData.course_id),
             order_index: parseInt(formData.order_index)
           };
           
           // Remove los from submitData as it's not part of lesson schema
           delete submitData.los;
           
           if (isEditing) {
             // Update existing lesson
             const updateData = { ...submitData, id: editingLessonId };
             console.log("Updating lesson: ", updateData);
             await lessonAPI.updateLesson(updateData);
             
             // Create/update LOS for existing lesson
             await createLOSForLesson(editingLessonId);
           } else {
             // Create new lesson
             console.log("Creating lesson: ", submitData);
             const response = await lessonAPI.createLesson(submitData);
             console.log('Lesson creation response:', response);
             
             // Get the created lesson ID from response
             const lessonId = response.data?.id || response.id;
             if (lessonId) {
               // Create LOS for the new lesson
               await createLOSForLesson(lessonId);
             } else {
               console.warn('Could not get lesson ID from response:', response);
             }
           }
           
           setIsModalOpen(false);
           resetForm();
           fetchLessons();
         } catch (error) {
           console.error(`Error ${isEditing ? 'updating' : 'creating'} lesson:`, error);
         } finally {
           setIsSubmitting(false);
         }
       };

       const handleEditLesson = async (lesson) => {
         setIsEditing(true);
         setEditingLessonId(lesson.id);
         fetchLos(lesson.id);
         
         // Load existing LOS for the lesson
         let existingLOS = [{ name: '' }]; // Default LOS
         
         setFormData({
           title: lesson.title || '',
           description: lesson.description || '',
           content: lesson.content || '',
           course_id: lesson.course_id ? String(lesson.course_id) : '',
           order_index: lesson.order_index || 0,
           video_url: lesson.video_url || '',
           los: existingLOS
         });
         setIsModalOpen(true);
       };

       const handleDeleteLesson = async (lessonId) => {
         if (!window.confirm('Are you sure you want to delete this lesson? This action cannot be undone.')) {
           return;
         }
         
         try {
           setDeletingLessonId(lessonId);
           console.log('Deleting lesson with ID:', lessonId);
           await lessonAPI.deleteLesson(lessonId);
           fetchLessons();
         } catch (error) {
           console.error('Error deleting lesson:', error);
           alert('Failed to delete lesson. Please try again.');
         } finally {
           setDeletingLessonId(null);
         }
       };

       const handleCreateLessonContent = (lesson) => {
         setSelectedLessonForContent(lesson);
         setIsViewingContents(false);
         setExistingLessonContents([]);
         setIsContentModalOpen(true);
       };
       
       const handleViewLessonContents = async (lesson) => {
         try {
           setSelectedLessonForContent(lesson);
           setIsViewingContents(true);
           
           // Fetch existing lesson contents
           const response = await lessonContentAPI.getLessonContentsByLessonId(lesson.id);
           const contents = response.data || [];
           setExistingLessonContents(contents);
           
           setIsContentModalOpen(true);
         } catch (error) {
           console.error('Error fetching lesson contents:', error);
           alert('Failed to load lesson contents. Please try again.');
         }
       };

       const handleContentDeleted = async (deletedContentId) => {
         console.log('Content deleted:', deletedContentId);
         // Refresh the lesson contents after deletion
         if (selectedLessonForContent) {
           try {
             const response = await lessonContentAPI.getLessonContentsByLessonId(selectedLessonForContent.id);
             const updatedContents = response.data || [];
             setExistingLessonContents(updatedContents);
           } catch (error) {
             console.error('Error refreshing lesson contents after deletion:', error);
           }
         }
       };
       
       const handleContentModalClose = () => {
         setIsContentModalOpen(false);
         setSelectedLessonForContent(null);
         setIsViewingContents(false);
         setExistingLessonContents([]);
       };

       const handleContentCreated = () => {
         // Optionally refresh lessons or show success message
         console.log('Lesson content created successfully');
       };

       const resetForm = () => {
         setFormData({
           title: '',
           description: '',
           content: '',
           course_id: '',
           order_index: 0,
           video_url: '',
           los: [{ name: '' }]
         });
         setIsEditing(false);
         setEditingLessonId(null);
       };

       const handleCloseModal = () => {
         setIsModalOpen(false);
         resetForm();
       };

       const fetchCourses = async () => {
        try {
          setCoursesLoading(true);
          const response = await courseAPI.getAllCourses();
          setCourses(response.data || []);
        } catch (error) {
          console.error('Error fetching courses:', error);
          setCourses([]);
        } finally {
          setCoursesLoading(false);
        }
      };

      const fetchLos = async (lessonId) => {
        try {
          const response = await losAPI.getLOSByLessonId(lessonId);
          setLos(response.data || []);
        }
        catch (error) {
          console.error('Error fetching LOS:', error);
          setLos([]);
        }
      }

      return (
         <div className="max-w-6xl mx-auto p-9">
           <div className="mb-8 border-b border-gray-200 pb-4">
             <div className="flex justify-between items-center">
               <div>
                 <h1 className="text-3xl font-bold text-gray-900 mb-2">Lessons</h1>
                 <p className="text-lg text-gray-600">Manage course lessons</p>
               </div>
               <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              + Add Lesson
            </button>
             </div>
           </div>

           <DataTable
             columns={columns}
             data={lessons}
             searchKey="title"
             searchPlaceholder="Search lessons..."
             loading={loading}
             emptyMessage="No lessons found."
           />

           <Modal isOpen={isModalOpen} onClose={handleCloseModal} disableBackdropClose={true}>
             <form onSubmit={handleSubmit}>
               <ModalHeader>
                 <h2 className="text-xl font-semibold">
                   {isEditing ? 'Edit Lesson' : 'Add New Lesson'}
                 </h2>
               </ModalHeader>

               <ModalBody className="space-y-4">
                 <div>
                   <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                     Title *
                   </label>
                   <Input
                     id="title"
                     name="title"
                     type="text"
                     value={formData.title}
                     onChange={handleInputChange}
                     required
                     placeholder="Enter lesson title"
                   />
                 </div>

                 <div>
                   <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                     Description
                   </label>
                   <Input
                     id="description"
                     name="description"
                     value={formData.description}
                     onChange={handleInputChange}
                     placeholder="Enter lesson description"
                   />
                 </div>

                 {/* <div>
                   <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                     Content
                   </label>
                   <textarea
                     id="content"
                     name="content"
                     value={formData.content}
                     onChange={handleInputChange}
                     rows={5}
                     className="w-full px-2 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     placeholder="Enter lesson content"
                   />
                 </div> */}

                 <div>
                   <label htmlFor="course_id" className="block text-sm font-medium text-gray-700 mb-1">
                     Course *
                   </label>
                   <select
                     id="course_id"
                     name="course_id"
                     value={formData.course_id}
                     onChange={handleInputChange}
                     required
                     disabled={coursesLoading}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {coursesLoading ? 'Loading courses...' : 'Select a course'}
                    </option>
                    {!coursesLoading && courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title || `Course ${course.id}`}
                      </option>
                    ))}
                  </select>
                 </div>

                 <div>
                   <label htmlFor="order_index" className="block text-sm font-medium text-gray-700 mb-1">
                     Order Index
                   </label>
                   <Input
                     id="order_index"
                     name="order_index"
                     type="number"
                     value={formData.order_index}
                     onChange={handleInputChange}
                     min="0"
                     placeholder="Enter order index"
                   />
                 </div>

                 {/* Learning Outcome Standards (LOS) Section */}
                 <div>
                   <div className="flex items-center justify-between mb-2">
                     <label className="block text-sm font-medium text-gray-700">
                       Learning Outcome Standards (LOS)
                     </label>
                     <button
                       type="button"
                       onClick={addLOS}
                       className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                     >
                       + Add LOS
                     </button>
                   </div>
                   <div className="space-y-2">
                     {formData.los.map((los, index) => (
                       <div key={index} className="flex items-center space-x-2">
                         <Input
                           value={los.name}
                           onChange={(e) => handleLOSChange(index, e.target.value)}
                           placeholder={`Students will be able to...`}
                           className="flex-1"
                         />
                         {formData.los.length > 1 && (
                           <button
                             type="button"
                             onClick={() => removeLOS(index)}
                             className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                             title="Remove LOS"
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                             </svg>
                           </button>
                         )}
                       </div>
                     ))}
                   </div>
                 </div>

                 {/* <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Video Upload
                   </label>
                   <FileUpload
                     onUploadSuccess={handleVideoUploadSuccess}
                     onUploadError={handleVideoUploadError}
                     accept="video/*"
                     maxSize={100 * 1024 * 1024}
                     folder="lesson-videos"
                     showPreview={false}
                     currentUrl={formData.video_url}
                   />
                   {formData.video_url && (
                     <div className="mt-2">
                       <p className="text-xs text-green-600">Video uploaded successfully</p>
                       <a 
                         href={formData.video_url} 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="text-xs text-blue-600 hover:underline"
                       >
                         View uploaded video
                       </a>
                     </div>
                   )}
                 </div> */}
               </ModalBody>

               <ModalFooter>
                 <Button
                   type="button"
                   onClick={handleCloseModal}
                   className="mr-2 bg-gray-500 hover:bg-gray-600"
                 >
                   Cancel
                 </Button>
                 <Button
                   type="submit"
                   disabled={isSubmitting}
                 >
                   {isSubmitting 
                     ? (isEditing ? 'Updating...' : 'Creating...') 
                     : (isEditing ? 'Update Lesson' : 'Create Lesson')
                   }
                 </Button>
               </ModalFooter>
             </form>
           </Modal>

           <LessonContentModal
             isOpen={isContentModalOpen}
             onClose={handleContentModalClose}
             lessonId={selectedLessonForContent?.id}
             lessonTitle={selectedLessonForContent?.title}
             onSuccess={handleContentCreated}
             isViewingMode={isViewingContents}
             existingContents={existingLessonContents}
             onContentDeleted={handleContentDeleted}
           />

           {/* LOS Management Modal */}
           <Modal 
             isOpen={isLOSModalOpen} 
             onClose={handleCloseLOSModal}
             disableBackdropClose={true}
             className="max-w-4xl mx-auto max-h-[90vh] flex flex-col"
           >
             <ModalHeader>
               <h2 className="text-xl font-semibold">
                 Learning Outcome Standards - {selectedLessonForLOS?.title}
               </h2>
               <p className="text-gray-600">
                 Manage learning outcomes for this lesson
               </p>
             </ModalHeader>

             <ModalBody className="flex flex-col p-6 max-h-[70vh] overflow-hidden">
               {/* LOS Count Summary */}
               <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200 flex-shrink-0">
                 <div className="text-sm text-yellow-700 flex items-center justify-between">
                   <div>
                     <span className="font-medium">{lessonLOS.length} Learning Outcome Standard(s)</span> defined for this lesson
                   </div>
                   <div className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                     Total: {lessonLOS.length} LOS
                   </div>
                 </div>
               </div>

               {/* LOS List */}
               <div className="flex-1 min-h-0 overflow-hidden">
                 {losLoading ? (
                   <div className="text-center py-8 text-gray-500 border border-gray-200 rounded">
                     <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600 mb-2"></div>
                     <p>Loading Learning Outcome Standards...</p>
                   </div>
                 ) : lessonLOS.length > 0 ? (
                   <div className="h-full max-h-96 overflow-y-auto border border-gray-200 rounded p-3 space-y-3 scrollbar-thin scrollbar-thumb-yellow-300 scrollbar-track-gray-100">
                     {lessonLOS.map((los, index) => (
                       <div
                         key={los.id}
                         className="p-4 border border-yellow-200 rounded-lg bg-yellow-50"
                       >
                         <div className="flex items-start justify-between">
                           <div className="flex-1">
                             <div className="flex items-center space-x-2 mb-2">
                               <h4 className="text-lg font-medium text-yellow-900">
                                 LOS {index + 1}
                               </h4>
                               <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded">
                                 ID: {los.id}
                               </span>
                             </div>
                             
                             <div className="text-sm text-yellow-700">
                               <p className="font-medium mb-1">Learning Outcome:</p>
                               {editingLOSId === los.id ? (
                                 <div className="space-y-2">
                                   <textarea
                                     value={editingLOSValue}
                                     onChange={(e) => setEditingLOSValue(e.target.value)}
                                     className="w-full p-2 border border-yellow-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                     rows={3}
                                     placeholder="Enter learning outcome..."
                                   />
                                   <div className="flex space-x-2">
                                     <button
                                       onClick={() => handleUpdateLOS(los.id)}
                                       disabled={updatingLOSId === los.id}
                                       className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                     >
                                       {updatingLOSId === los.id ? 'Saving...' : 'Save'}
                                     </button>
                                     <button
                                       onClick={handleCancelEditLOS}
                                       disabled={updatingLOSId === los.id}
                                       className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
                                     >
                                       Cancel
                                     </button>
                                   </div>
                                 </div>
                               ) : (
                                 <p className="bg-white p-2 rounded border border-yellow-200">
                                   {los.name}
                                 </p>
                               )}
                             </div>

                             <div className="mt-2 text-xs text-gray-500">
                               <span>Lesson ID: {los.lesson_id}</span>
                               {los.created_at && (
                                 <span className="ml-4">
                                   Created: {new Date(los.created_at).toLocaleDateString()}
                                 </span>
                               )}
                             </div>
                           </div>
                           
                           <div className="flex flex-col space-y-1 ml-2">
                             {editingLOSId !== los.id && (
                               <button
                                 onClick={() => handleEditLOS(los)}
                                 disabled={editingLOSId !== null}
                                 className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                 title="Edit Learning Outcome Standard"
                               >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                 </svg>
                               </button>
                             )}
                             {editingLOSId !== los.id && (
                               <button
                                 onClick={() => handleDeleteLOS(los.id)}
                                 disabled={deletingLOSId === los.id || editingLOSId !== null}
                                 className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                 title="Delete Learning Outcome Standard"
                               >
                                 {deletingLOSId === los.id ? (
                                   <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                                 ) : (
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                   </svg>
                                 )}
                               </button>
                             )}
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <div className="text-center py-8 text-gray-500 border border-gray-200 rounded h-full flex items-center justify-center">
                     <div>
                       <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                       </svg>
                       <h3 className="text-lg font-medium text-gray-900 mb-2">No Learning Outcome Standards</h3>
                       <p>No learning outcomes have been defined for this lesson yet.</p>
                       <p className="text-sm mt-1">Edit the lesson to add learning outcome standards.</p>
                     </div>
                   </div>
                 )}
               </div>
             </ModalBody>

             <ModalFooter>
               <Button
                 onClick={handleCloseLOSModal}
                 className="mr-2 bg-gray-500 hover:bg-gray-600"
               >
                 Close
               </Button>
               <Button
                 onClick={() => {
                   handleCloseLOSModal();
                   handleEditLesson(selectedLessonForLOS);
                 }}
                 className="bg-yellow-600 hover:bg-yellow-700"
               >
                 Edit Learning Outcomes
               </Button>
             </ModalFooter>
           </Modal>
         </div>
       );
     };
     
     export default Lessons;
